import { InteractionStatus } from '@azure/msal-browser'
import axios, { CancelTokenSource } from 'axios'
import React, { useEffect, useRef, useState } from 'react'
import useErrorHandler from '../../hooks/useErrorHandler'
import { UserProfile, UserProfileFormValues } from '../../models/Profile'
import ProfileService from '../../services/ProfileService'
import Conditional from '../common/Conditional'
import WorkplaceFooter from '../common/WorkplaceFooter'
import Protected from '../Protected'
import { useMsal } from '@azure/msal-react'
import RecommendedDocument from './RecommendedDocument'
import RecommendedLink from './RecommendedLink'
import './Resources.css'
const Resources = () => {
  const { instance, inProgress } = useMsal()
  const mountedRef = useRef(false)
  const [isLoading, setLoading] = useState(true)
  const [, , handleErrors] = useErrorHandler()

  const [userProfile, setUserProfile] = useState<UserProfile>(
    new UserProfileFormValues()
  )
  const activeAccount = instance.getActiveAccount()
  const GOVCMS_BASE_URL = 'https://www.fairwork.gov.au'

  const OSP_BASE_URL =
    process.env.REACT_APP_REDIRECT_URI.split('/my-account')[0]
  useEffect(() => {
    mountedRef.current = true
    if (activeAccount && inProgress === InteractionStatus.None) {
      const source = axios.CancelToken.source()
      getUserProfile(source)
        .then((result) => {
          if (mountedRef.current) {
            setUserProfile(result)
          }
        })
        .catch((error) => {
          handleErrors(error)
        })
        .finally(() => {
          if (mountedRef.current) {
            setLoading(false)
          }
        })
    }

    return () => {
      mountedRef.current = false
    }
  }, [inProgress])

  const getUserProfile = async (source: CancelTokenSource) => {
    return await ProfileService.getCurrentUserProfile(source).then(
      (response) => {
        return response.result
      }
    )
  }

  return (
    <Protected>
      <div className="content-wrapper">
        <article className="main">
          <h1> Resources </h1>

          <h3>Online learning</h3>
          <div
            className="paragraph paragraph--type--link-cards-home-page live-tiles"
            id="onlineLearning"
            aria-label="Online learning"
          >
            <p>
              Our online courses will teach you skills and strategies to help
              you at work. &nbsp;The courses are free and interactive. They take
              around 20 - 40 minutes to complete.
            </p>
            {/* 1st tile */}
            <Conditional
              if={
                userProfile.resourceUserType === 0 ||
                userProfile.resourceUserType === 1
              }
            >
              <RecommendedLink
                name="Record-keeping and pay slips"
                url={
                  GOVCMS_BASE_URL +
                  '/tools-and-resources/online-learning-centre/record-keeping-and-pay-slips'
                }
              >
                Good record-keeping helps you protect and manage your business.
              </RecommendedLink>
            </Conditional>
            <Conditional if={userProfile.resourceUserType === 2}>
              <RecommendedLink
                name="Workplace laws for business advisers"
                url={
                  GOVCMS_BASE_URL +
                  '/online-learning-centre/workplace-laws-for-business-advisers'
                }
              >
                Learn how to help your clients find the right information and
                deal with common workplace issues.
              </RecommendedLink>
            </Conditional>
            <Conditional if={userProfile.resourceUserType === 3}>
              <RecommendedLink
                name="Difficult conversations in the workplace - employee course"
                url={
                  GOVCMS_BASE_URL +
                  '/online-learning-centre/difficult-conversations-in-the-workplace-employee-course'
                }
              >
                Learn the skills you need to confidently discuss workplace
                issues with your employer.
              </RecommendedLink>
            </Conditional>
            <Conditional
              if={
                userProfile.resourceUserType === 4 ||
                userProfile.resourceUserType === 5
              }
            >
              <RecommendedLink
                name="Starting a new job"
                url={
                  GOVCMS_BASE_URL + '/online-learning-centre/starting-a-new-job'
                }
              >
                Learn about your rights and the questions you should ask when
                starting a new job. Parents and teachers can use this course to
                help educate young people about starting work.
              </RecommendedLink>
            </Conditional>
            <Conditional if={userProfile.resourceUserType === 6}>
              <RecommendedLink
                name="Hiring employees"
                url={
                  GOVCMS_BASE_URL + '/online-learning-centre/hiring-employees'
                }
              >
                Learn about the steps involved in hiring new employees.
              </RecommendedLink>
            </Conditional>
            {/* 2nd tile */}
            <Conditional
              if={
                userProfile.resourceUserType === 0 ||
                userProfile.resourceUserType === 1
              }
            >
              <RecommendedLink
                name="Difficult conversations in the workplace - manager courses"
                url={
                  GOVCMS_BASE_URL +
                  '/online-learning-centre/difficult-conversations-in-the-workplace-manager-course'
                }
              >
                Learn the skills you need to discuss difficult workplace issues
                with your employees.
              </RecommendedLink>
            </Conditional>
            <Conditional if={userProfile.resourceUserType === 2}>
              <RecommendedLink // TODO Looks like a duplicate above
                name="Record-keeping and pay slips"
                url={
                  GOVCMS_BASE_URL +
                  '/tools-and-resources/online-learning-centre/record-keeping-and-pay-slips'
                }
              >
                Good record-keeping helps you protect and manage your business.
              </RecommendedLink>
            </Conditional>
            <Conditional if={userProfile.resourceUserType === 3}>
              <RecommendedLink
                name="Starting a new job"
                url={
                  GOVCMS_BASE_URL + '/online-learning-centre/starting-a-new-job'
                }
              >
                Learn about your rights and the questions you should ask when
                starting a new job. Parents and teachers can use this course to
                help educate young people about starting work.
              </RecommendedLink>
            </Conditional>
            <Conditional
              if={
                userProfile.resourceUserType === 4 ||
                userProfile.resourceUserType === 5
              }
            >
              <RecommendedLink // TODO Looks like a duplicate above
                name="Difficult conversations in the workplace - employee course"
                url={
                  GOVCMS_BASE_URL +
                  '/online-learning-centre/difficult-conversations-in-the-workplace-employee-course'
                }
              >
                Learn the skills you need to confidently discuss workplace
                issues with your employer.
              </RecommendedLink>
            </Conditional>
            <RecommendedLink
              name="Online learning centre"
              url={GOVCMS_BASE_URL + '/online-learning-centre'}
            >
              Our online courses will teach you skills and strategies to help
              you at work.
            </RecommendedLink>
          </div>

          <Conditional if={userProfile.resourceUserType != 6}>
            <h3>Templates</h3>
            <div
              className="paragraph paragraph--type--link-cards-home-page live-tiles"
              id="templates"
            >
              <p>
                We have interactive template tools and downloadable templates
                and letters to help you manage a wide range of workplace
                obligations.
              </p>
              {/* 1st Tile */}
              <Conditional
                if={
                  userProfile.resourceUserType === 0 ||
                  userProfile.resourceUserType === 1 ||
                  userProfile.resourceUserType === 2
                }
              >
                <RecommendedLink
                  name="End of probation letter"
                  url={
                    OSP_BASE_URL +
                    '/tools-and-resources/templates/end-of-probation'
                  }
                >
                  This tool will help you prepare a letter to advise your
                  employee that they have successfully completed their probation
                  period.
                </RecommendedLink>
              </Conditional>
              <Conditional
                if={
                  userProfile.resourceUserType === 3 ||
                  userProfile.resourceUserType === 4 ||
                  userProfile.resourceUserType === 5
                }
              >
                <RecommendedLink
                  name="Request for records"
                  url={
                    OSP_BASE_URL +
                    '/tools-and-resources/templates/request-for-records'
                  }
                >
                  This tool will help you prepare a request for records.
                  Employees can request a copy of their employment records from
                  a current or former employer.
                </RecommendedLink>
              </Conditional>
              {/* 2nd Tile */}
              <Conditional
                if={
                  userProfile.resourceUserType === 0 ||
                  userProfile.resourceUserType === 1 ||
                  userProfile.resourceUserType === 2 ||
                  userProfile.resourceUserType === 3 ||
                  userProfile.resourceUserType === 4
                }
              >
                <RecommendedDocument
                  name="Starting a new job checklist"
                  urlDoc="https://www.fairwork.gov.au/sites/default/files/migration/712/A-guide-to-starting-a-new-job.docx"
                  urlPdf="https://www.fairwork.gov.au/sites/default/files/migration/712/A-guide-to-starting-a-new-job.pdf"
                >
                  Download a starting a new job checklist to help make sure you
                  have all the information you need when starting a new job.
                </RecommendedDocument>
              </Conditional>
              <Conditional if={userProfile.resourceUserType === 5}>
                <RecommendedDocument
                  name="Request for flexible working arrangements"
                  urlDoc="https://www.fairwork.gov.au/sites/default/files/migration/766/Request-for-flexible-working-arrangements-letter-template.doc"
                  urlPdf="https://www.fairwork.gov.au/sites/default/files/migration/766/request-for-flexible-working-arrangements-letter-template.pdf"
                >
                  Download a request for flexible working arrangements template
                  you can tailor to request flexible working arrangements from
                  your employer.
                </RecommendedDocument>
              </Conditional>
              {/* 3rd Tile */}
              <Conditional if={userProfile.resourceUserType != 6}>
                <RecommendedLink
                  name="Templates"
                  url={GOVCMS_BASE_URL + '/tools-and-resources/templates'}
                  linkText="View all templates"
                >
                  Our templates can help you manage a range of workplace
                  obligations.
                </RecommendedLink>
              </Conditional>
            </div>
          </Conditional>

          <Conditional
            if={
              userProfile.resourceUserType === 0 ||
              userProfile.resourceUserType === 1 ||
              userProfile.resourceUserType === 2
            }
          >
            <h3>Best practice guides</h3>
            <div
              className="paragraph paragraph--type--link-cards-home-page live-tiles "
              id="bestPracticeGuides"
            >
              <p>
                Our best practice guides aim to help small businesses and
                employees with a range of workplace issues. By adopting best
                practice initiatives, employers and employees can achieve
                happier, fairer and more productive workplaces.
              </p>
              <Conditional if={userProfile.resourceUserType === 0}>
                <RecommendedLink
                  name="Small business and the Fair Work Act"
                  url={
                    GOVCMS_BASE_URL +
                    '/tools-and-resources/best-practice-guides/small-business-and-the-fair-work-act'
                  }
                  linkText="Go to the best pratice guide"
                >
                  This best practice guide is for small business owners and
                  managers. It explains your basic obligations under the Fair
                  Work Act and how you can take a best practice approach to
                  employment and workplace issues.
                </RecommendedLink>
              </Conditional>
              <Conditional
                if={
                  userProfile.resourceUserType === 1 ||
                  userProfile.resourceUserType === 2 ||
                  userProfile.resourceUserType === 0
                }
              >
                <RecommendedLink
                  name="Effective dispute resolution"
                  url={
                    GOVCMS_BASE_URL +
                    '/tools-and-resources/best-practice-guides/effective-dispute-resolution'
                  }
                  linkText="Go to the best pratice guide"
                >
                  This best practice guide is for employers and managers. It
                  explains how you can use best practice to avoid, manage and
                  resolve disputes in your business.
                </RecommendedLink>
              </Conditional>
              <Conditional if={userProfile.resourceUserType === 1}>
                <RecommendedLink
                  name="Flexible working arrangements"
                  url={
                    GOVCMS_BASE_URL +
                    '/tools-and-resources/best-practice-guides/flexible-working-arrangements'
                  }
                  linkText="Go to the best pratice guide"
                >
                  This best practice guide is for managers and employers. It
                  explains the advantages of taking a best practice approach to
                  flexible working arrangements.
                </RecommendedLink>
              </Conditional>
              <Conditional if={userProfile.resourceUserType === 2}>
                <RecommendedLink
                  name="Consultation and cooperation in the workplace"
                  url={
                    GOVCMS_BASE_URL +
                    '/tools-and-resources/best-practice-guides/consultation-and-cooperation-in-the-workplace'
                  }
                  linkText="Go to the best pratice guide"
                >
                  This best practice guide is for managers and employers. It
                  explains the advantages of taking a best practice approach to
                  consultation and cooperation in your business.
                </RecommendedLink>
              </Conditional>
              <Conditional
                if={
                  userProfile.resourceUserType === 0 ||
                  userProfile.resourceUserType === 1 ||
                  userProfile.resourceUserType === 2
                }
              >
                <RecommendedLink
                  name="Best practice guides"
                  url={
                    GOVCMS_BASE_URL +
                    '/tools-and-resources/best-practice-guides'
                  }
                  linkText="View all guides"
                >
                  Our best practice guides cover a range of different workplace
                  issues.
                </RecommendedLink>
              </Conditional>
            </div>
          </Conditional>

          <h3>Resolving common workplace problems</h3>
          <div
            className="paragraph paragraph--type--link-cards-home-page live-tiles"
            id="workplaceProblems"
          >
            <p>
              Problems happen in every workplace from time to time. If
              they&apos;re not dealt with, they can make workplaces unproductive
              and difficult for everyone.
            </p>
            {/* 1st Tile */}
            <Conditional if={userProfile.resourceUserType === 0}>
              <RecommendedLink
                name="I think I've underpaid my employee"
                url={
                  GOVCMS_BASE_URL +
                  '/workplace-problems/common-workplace-problems/i-think-ive-underpaid-my-employee'
                }
                linkText="Go to the workplace problem"
              >
                Employees have to be paid at least their minimum pay rates and
                entitlements.
              </RecommendedLink>
            </Conditional>
            <Conditional
              if={
                userProfile.resourceUserType === 1 ||
                userProfile.resourceUserType === 2
              }
            >
              <RecommendedLink
                name="I'm not sure what to pay my employees"
                url={
                  GOVCMS_BASE_URL +
                  '/workplace-problems/common-workplace-problems/im-not-sure-what-to-pay-my-employees'
                }
                linkText="Go to the workplace problem"
              >
                Not knowing what to pay staff can be a common problem for
                employers.
              </RecommendedLink>
            </Conditional>

            <Conditional
              if={
                userProfile.resourceUserType === 3 ||
                userProfile.resourceUserType === 5
              }
            >
              <RecommendedLink
                name="My pay doesn't seem right"
                url={
                  GOVCMS_BASE_URL +
                  '/workplace-problems/common-workplace-problems/my-pay-doesnt-seem-right'
                }
                linkText="Go to the workplace problem"
              >
                Problems and questions about pay are the most common problems
                employees experience in the workplace that we hear about
              </RecommendedLink>
            </Conditional>
            <Conditional if={userProfile.resourceUserType === 4}>
              <RecommendedLink
                name="I've been fired and I'm not sure what I'm entitled to"
                url={
                  GOVCMS_BASE_URL +
                  '/workplace-problems/common-workplace-problems/ive-been-fired-and-im-not-sure-what-im-entitled-to'
                }
                linkText="Go to the workplace problem"
              >
                Follow our step-by-step guide to see if you&apos;ve been given
                enough notice and what your entitlements are.
              </RecommendedLink>
            </Conditional>
            <Conditional if={userProfile.resourceUserType === 6}>
              <RecommendedLink
                name="I'm a migrant worker being treated unfairly"
                url={
                  GOVCMS_BASE_URL +
                  '/workplace-problems/common-workplace-problems/im-a-migrant-worker-being-treated-unfairly'
                }
                linkText="Go to the workplace problem"
              >
                Migrant workers and visa holders, including international
                students, have the same workplace rights as all other employees
                in Australia.
              </RecommendedLink>
            </Conditional>
            {/* 2nd Tile */}
            <Conditional
              if={
                userProfile.resourceUserType === 0 ||
                userProfile.resourceUserType === 1 ||
                userProfile.resourceUserType === 2
              }
            >
              <RecommendedLink
                name="I'm not sure which award covers my business"
                url={
                  GOVCMS_BASE_URL +
                  '/workplace-problems/common-workplace-problems/im-not-sure-which-award-covers-my-business'
                }
                linkText="Go to the workplace problem"
              >
                Finding out which award applies to your business is essential to
                understanding your obligations and paying your employees their
                correct entitlements.
              </RecommendedLink>
            </Conditional>
            <Conditional
              if={
                userProfile.resourceUserType === 3 ||
                userProfile.resourceUserType === 5
              }
            >
              <RecommendedLink
                name="I'm not getting pay slips"
                url={
                  GOVCMS_BASE_URL +
                  '/workplace-problems/common-workplace-problems/im-not-getting-pay-slips'
                }
                linkText="Go to the workplace problem"
              >
                As an employee, you should be given a pay slip within one
                working day of being paid, even if you&apos;re on leave.
              </RecommendedLink>
            </Conditional>
            <Conditional if={userProfile.resourceUserType === 4}>
              <RecommendedLink // TODO Looks like a duplicate above
                name="My pay doesn't seem right"
                url={
                  GOVCMS_BASE_URL +
                  '/workplace-problems/common-workplace-problems/my-pay-doesnt-seem-right'
                }
                linkText="Go to the workplace problem"
              >
                Problems and questions about pay are the most common problems
                employees experience in the workplace that we hear about.
              </RecommendedLink>
            </Conditional>
            <Conditional if={userProfile.resourceUserType === 6}>
              <RecommendedLink
                name="I think I could be an employee not a contractor"
                url={
                  GOVCMS_BASE_URL +
                  '/common-workplace-problems/i-think-i-could-be-an-employee-not-a-contractor'
                }
                linkText="Go to the workplace problem"
              >
                Sham contracting happens when an employer attempts to disguise
                an employment relationship as an independent contracting
                arrangement.
              </RecommendedLink>
            </Conditional>
            {/* 3rd Tile */}
            <RecommendedLink
              name="Resolving common workplace problems"
              url={
                GOVCMS_BASE_URL +
                '/workplace-problems/common-workplace-problems'
              }
              linkText="View all common workplace problems"
            >
              We find that most problems can be resolved by addressing the issue
              in the workplace as quickly as possible.
            </RecommendedLink>
          </div>
        </article>
      </div>
      <p></p>
      <WorkplaceFooter userProfile={userProfile} isLoading={isLoading} />
    </Protected>
  )
}

export default Resources
